<template>
  <vx-card title="Umsatz nach Zahlungsart">
    <div class="flex mb-4">
      <vs-button
        v-for="p in periods"
        :key="p.value"
        :type="activePeriod === p.value ? 'filled' : 'border'"
        size="small"
        class="mr-2"
        @click="changePeriod(p.value)"
      >
        {{ p.label }}
      </vs-button>
    </div>
    <vue-apex-charts
      type="bar"
      height="350"
      :options="chartOptions"
      :series="series"
    />
  </vx-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

const CATEGORY_ORDER = [
  "Bar Website",
  "Bar App",
  "PayPal Website",
  "PayPal App",
  "Stripe App",
  "Sonstige",
];

const PAYMENT_COLORS = {
  "Bar Website": "#C41E2A",
  "Bar App": "#FF6B6B",
  "PayPal Website": "#2ECC71",
  "PayPal App": "#A3E635",
  "Stripe App": "#1ABC9C",
  Sonstige: "#555555",
};

export default {
  name: "AnalyticsRevenueByPayment",
  components: { VueApexCharts },
  data() {
    return {
      activePeriod: "daily",
      periods: [
        { label: "Täglich", value: "daily" },
        { label: "Wöchentlich", value: "weekly" },
        { label: "Monatlich", value: "monthly" },
      ],
    };
  },
  computed: {
    rawData() {
      return this.$store.state.analytics.revenueByPayment || [];
    },
    dates() {
      const set = new Set(this.rawData.map((item) => item._id.date));
      return Array.from(set).sort();
    },
    paymentCategories() {
      const found = new Set(this.rawData.map((item) => item._id.payment));
      return CATEGORY_ORDER.filter((cat) => found.has(cat));
    },
    dailyTotals() {
      return this.dates.map((date) => {
        let total = 0;
        this.rawData.forEach((item) => {
          if (item._id.date === date) total += item.revenue;
        });
        return Math.round(total * 100) / 100;
      });
    },
    series() {
      const paymentSeries = this.paymentCategories.map((cat) => ({
        name: cat,
        data: this.dates.map((date) => {
          const entry = this.rawData.find(
            (item) => item._id.date === date && item._id.payment === cat
          );
          return entry ? Math.round(entry.revenue * 100) / 100 : 0;
        }),
      }));

      // Invisible series for total labels on top
      paymentSeries.push({
        name: "Gesamt",
        data: this.dailyTotals.map(() => 0),
      });

      return paymentSeries;
    },
    labels() {
      return this.dates.map((d) => this.formatDate(d));
    },
    chartOptions() {
      const totals = this.dailyTotals;
      const colors = this.paymentCategories.map(
        (cat) => PAYMENT_COLORS[cat] || "#6B7280"
      );
      colors.push("transparent");

      return {
        chart: {
          stacked: true,
          toolbar: { show: false },
          foreColor: "#c2c6dc",
          background: "transparent",
        },
        theme: { mode: "dark" },
        grid: { borderColor: "rgba(255,255,255,0.06)" },
        plotOptions: {
          bar: { columnWidth: "55%", borderRadius: 4 },
        },
        xaxis: { categories: this.labels },
        yaxis: {
          labels: { formatter: (val) => val.toFixed(0) + " €" },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            if (opts.seriesIndex === opts.w.config.series.length - 1) {
              return totals[opts.dataPointIndex].toFixed(0) + " €";
            }
            return "";
          },
          offsetY: -4,
          style: {
            fontSize: "11px",
            fontWeight: 600,
            colors: ["#fff"],
          },
          background: { enabled: false },
          dropShadow: { enabled: false },
        },
        colors: colors,
        fill: { opacity: 0.75 },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              if (opts.seriesIndex === opts.w.config.series.length - 1)
                return null;
              return val.toFixed(2) + " €";
            },
          },
        },
        legend: {
          position: "top",
          labels: { colors: "#c2c6dc" },
          customLegendItems: this.paymentCategories,
          markers: {
            fillColors: this.paymentCategories.map(
              (cat) => PAYMENT_COLORS[cat] || "#6B7280"
            ),
          },
        },
      };
    },
  },
  methods: {
    formatDate(dateStr) {
      const parts = dateStr.split("-");
      if (this.activePeriod === "monthly") {
        const months = [
          "Jan",
          "Feb",
          "Mär",
          "Apr",
          "Mai",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Okt",
          "Nov",
          "Dez",
        ];
        return `${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
      }
      return `${parts[2]}.${parts[1]}`;
    },
    changePeriod(period) {
      this.activePeriod = period;
      const daysMap = { daily: 30, weekly: 90, monthly: 730 };
      this.$store.dispatch("analytics/fetchRevenueByPayment", {
        period,
        days: daysMap[period],
      });
    },
  },
};
</script>
